import type { CSSProperties, ReactNode } from "react";
import { Chip, Loading, Problem } from "./index.tsx";

/**
 * A job's floor plans as a list (T68's Customer 360 card, T73's Estimating tab).
 *
 * One component, so the customer record and the estimator draw a plan the same way: its name,
 * floors, walls and when it was last edited, the editor's issue counts, and Open. It lives here
 * rather than in either module because modules may not import each other; it is presentational,
 * and each caller reads the plans and hands in its own in-app link, so this package gains no
 * router and no data client.
 */

/** A plan as the list draws it: the fields of `PlanSummary` it reads. */
export interface FloorPlanListRow {
  id: string;
  name: string;
  floorCount: number;
  wallCount: number;
  updatedAt: string;
  /** The editor's issue counts at its last save (plans-contract 0.2.1); absent from an older editor's save. */
  issues?: { blocks: number; checks: number };
}

/** The caller's in-app link, so a click navigates in place rather than reloading the shell. */
export type FloorPlanLink = (p: { href: string; ariaLabel?: string; style?: CSSProperties; children: ReactNode }) => ReactNode;

const edited = (iso: string) => new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
const note: CSSProperties = { margin: 0, fontSize: 12.5 };

/**
 * Reading, failed, none, or the rows. `empty` is the sentence for none, which differs by where
 * the list sits (a customer's jobs, or one job).
 */
export function FloorPlanList({ plans, loading, error, empty, Link }: {
  plans: readonly FloorPlanListRow[];
  loading: boolean;
  error: unknown;
  empty: string;
  Link: FloorPlanLink;
}) {
  if (error) return <Problem error={error} fallback="The floor plans could not be read." />;
  if (loading) return <Loading label="Reading the floor plans" compact />;
  if (plans.length === 0) return <p className="muted" style={note}>{empty}</p>;
  return (
    <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "grid", gap: 10, fontSize: 13 }}>
      {plans.map((p) => (
        <li key={p.id} style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <div style={{ display: "grid", gap: 2, minWidth: 0 }}>
            <b style={{ color: "var(--ink-head)" }}>{p.name}</b>
            <span className="muted" style={{ fontSize: 12 }}>
              {p.floorCount} {p.floorCount === 1 ? "floor" : "floors"} · {p.wallCount} {p.wallCount === 1 ? "wall" : "walls"} · edited {edited(p.updatedAt)}
            </span>
          </div>
          <span style={{ marginLeft: "auto", display: "inline-flex", gap: 6, flexWrap: "wrap", justifyContent: "flex-end" }}>
            {p.issues && p.issues.blocks > 0 && <Chip icon="✕" tone="bad">{p.issues.blocks} {p.issues.blocks === 1 ? "blocks" : "block"} export</Chip>}
            {p.issues && p.issues.checks > 0 && <Chip icon="⚠" tone="warn">{p.issues.checks} to check</Chip>}
          </span>
          {Link({
            href: `/plans?plan=${encodeURIComponent(p.id)}`,
            ariaLabel: `Open ${p.name}`,
            style: { color: "var(--link)", textDecoration: "none", fontWeight: 600, fontSize: 12.5 },
            children: "Open",
          })}
        </li>
      ))}
    </ul>
  );
}
